import { Suspense, useState } from 'react';
import _ from 'lodash';
import { faCommentsDollar } from '@fortawesome/pro-regular-svg-icons';
import { Slide } from '@progress/kendo-react-animation';
import {
    AdjustmentGridView,
    DisputeButtons,
    DisputeDetailHeader,
    DisputeDocuments,
    DisputeStatusBadge,
    Icon,
    InvoiceDetailSidebar,
    LoaderOverlay,
    PaymentDetailSidebar,
    SuspenseBoundary,
    TrackingHistory
} from 'components';
import { AdjustmentTypeMap } from 'config';
import { useAdjustmentCategory, useAdjustmentType, useDisputeParam } from 'hooks';
import { useDisputeDetails, useRefreshDisputeDetails } from 'state';
import { PaymentDetail, InvoiceDetail } from 'types';
import styles from './DisputeDetailsPage.module.scss';

export default function DisputeDetailsPage() {
    const disputeId = useDisputeParam();
    const refresh = useRefreshDisputeDetails(disputeId);

    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving dispute details for dispute ${disputeId}`}
            onReset={refresh}
        >
            <DisputeDetailsComponent />
        </SuspenseBoundary>
    );
}

const DisputeDetailsComponent = () => {
    const disputeDetails = useDisputeDetails();
    const { adjustmentTypeId, disputeStatusId, paymentDetailLines = [], invoiceDetailLines = [] } = disputeDetails;
    const isInvoice = !_.isEmpty(invoiceDetailLines);
    const disputeLines = isInvoice ? invoiceDetailLines : paymentDetailLines;
    const category = useAdjustmentType(adjustmentTypeId, AdjustmentTypeMap);
    const gridProps = useAdjustmentCategory(disputeLines, AdjustmentTypeMap, category);

    const [selectedDetails, setSelectedDetails] = useState<any[]>([])
    const [expanded, setExpanded] = useState<boolean>(false);

    return (
        <div className={'details-page'}>
            <div className={styles.pageHeader}>
                <span className={'details-page-title'}><Icon icon={faCommentsDollar} /> Dispute Detail </span>
            </div>
            <div>
                <DisputeButtons />
            </div>
            <div className={'details-page-main'}>
                <div className={'details-page-main-header'}>
                    <DisputeDetailHeader disputeDetails={disputeDetails} />
                </div>
                <div className={styles.details}>
                    <div className={styles.bodyLeftSide}>
                        <div>
                            <div className={styles.adjustmentsTitle}>
                                <span>Disputed Adjustments</span>
                            </div>
                            <AdjustmentGridView
                                {...gridProps}
                                selectMode="single"
                                selectedDetails={selectedDetails ?? []}
                                setSelectedDetails={setSelectedDetails}
                                setExpanded={setExpanded}
                                showTotal={true}
                            />
                            <Suspense
                                fallback={
                                    <LoaderOverlay isLoading container height="140px">
                                        Loading dispute documents...
                                    </LoaderOverlay>
                                }
                            >
                                <DisputeDocuments />
                            </Suspense>
                        </div>
                    </div>
                    <div className={styles.bodyRightSide}>
                        <div className={styles.history}>
                            <div className={styles.trackingHeader}>
                                <div className={styles.title}>Dispute Timeline</div>
                                <DisputeStatusBadge status={disputeStatusId} />
                            </div>
                            <TrackingHistory dispute={disputeDetails} />
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <Slide
                    appear={expanded}
                    direction="left"
                    className={styles.sidebar}
                >
                    {expanded && selectedDetails?.length === 1 &&
                        <div className={styles.sidebarContent}>
                            {isInvoice ? (
                                <InvoiceDetailSidebar
                                    allowDispute={false}
                                    selectedDetail={(selectedDetails && selectedDetails[0]) as InvoiceDetail}
                                    setSelectedDetails={setSelectedDetails}
                                    expanded={expanded}
                                    setExpanded={setExpanded}
                                />
                            ) : (
                                <PaymentDetailSidebar
                                    allowDispute={false}
                                    selectedDetail={(selectedDetails && selectedDetails[0]) as PaymentDetail}
                                    setSelectedDetails={setSelectedDetails}
                                    expanded={expanded}
                                    setExpanded={setExpanded}
                                />
                            )}
                        </div>
                    }
                </Slide>
            </div>
        </div>
    )
}
